import { useState, useCallback, useEffect, useRef } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OnboardingStatus {
  status: "not_started" | "in_progress" | "completed";
  currentStep: number;
  completedSteps: string[];
  skippedSteps: string[];
}

interface ProfileData {
  first_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  role_preference: string | null;
  school_name: string | null;
  grade_level: string | null;
  interests: string[] | null;
}

interface OnboardingProps {
  apiRequest: (path: string, init?: RequestInit) => Promise<Response>;
  onComplete: () => void;
  onSignOut: () => void;
  initialStatus: OnboardingStatus | null;
  initialProfile: ProfileData | null;
}

type Step = "basic_info" | "school_info" | "interests" | "review";

const STEP_LABELS: Record<Step, string> = {
  basic_info: "Basic Info",
  school_info: "School",
  interests: "Interests",
  review: "Review",
};

const STEPS: Step[] = ["basic_info", "school_info", "interests", "review"];

// ---------------------------------------------------------------------------
// Suggested interests
// ---------------------------------------------------------------------------

const SUGGESTED_INTERESTS = [
  "Mathematics",
  "Science",
  "English",
  "History",
  "Art",
  "Music",
  "Computer Science",
  "Physical Education",
  "Foreign Languages",
  "Business",
  "Engineering",
  "Medicine",
  "Law",
  "Psychology",
  "Environmental Science",
  "Creative Writing",
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Onboarding({
  apiRequest,
  onComplete,
  onSignOut,
  initialStatus,
  initialProfile,
}: OnboardingProps): React.ReactElement {
  // Determine starting step from initial status
  const getInitialStep = (): Step => {
    if (!initialStatus || initialStatus.status === "not_started") {
      return "basic_info";
    }
    if (initialStatus.completedSteps.includes("basic_info")) {
      if (
        initialStatus.completedSteps.includes("school_info") ||
        initialStatus.skippedSteps.includes("school_info")
      ) {
        if (
          initialStatus.completedSteps.includes("interests") ||
          initialStatus.skippedSteps.includes("interests")
        ) {
          return "review";
        }
        return "interests";
      }
      return "school_info";
    }
    return "basic_info";
  };

  const [currentStep, setCurrentStep] = useState<Step>(getInitialStep);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedSteps, setCompletedSteps] = useState<string[]>(
    initialStatus?.completedSteps ?? []
  );

  // Basic info fields — pre-populate from saved profile data
  const [firstName, setFirstName] = useState(initialProfile?.first_name ?? "");
  const [lastName, setLastName] = useState(initialProfile?.last_name ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(initialProfile?.date_of_birth ?? "");
  const [rolePreference, setRolePreference] = useState<"student" | "counselor" | "">(
    (initialProfile?.role_preference as "student" | "counselor" | null) ?? ""
  );

  // Inline field validation errors (shown on submit attempt)
  const [roleError, setRoleError] = useState<string | null>(null);

  // School info fields (optional) — pre-populate from saved profile data
  const [schoolName, setSchoolName] = useState(initialProfile?.school_name ?? "");
  const [gradeLevel, setGradeLevel] = useState(initialProfile?.grade_level ?? "");

  // Interests (optional) — pre-populate from saved profile data
  const [selectedInterests, setSelectedInterests] = useState<string[]>(
    initialProfile?.interests ?? []
  );
  const [customInterest, setCustomInterest] = useState("");

  const currentStepIndex = STEPS.indexOf(currentStep);

  // Auto-focus the active form when the step changes so Enter submits immediately
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    formRef.current?.focus();
  }, [currentStep]);

  /** Submit the form on Enter when the form element itself (or a non-input child) has focus. */
  const handleFormKeyDown = useCallback((event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Enter" || event.defaultPrevented) return;

    const target = event.target as HTMLElement;
    const tag = target.tagName;

    // Let native behavior handle Enter on inputs, textareas, selects, and submit buttons
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
    if (tag === "BUTTON" && (target as HTMLButtonElement).type === "submit") return;

    event.preventDefault();
    formRef.current?.requestSubmit();
  }, []);

  const isUnderAge = (() => {
    if (!dateOfBirth || !/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) {
      return false;
    }
    const dob = new Date(dateOfBirth);
    if (isNaN(dob.getTime())) {
      return false;
    }
    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    const dayDiff = today.getDate() - dob.getDate();
    const adjustedAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;
    return adjustedAge < 18;
  })();

  // ---------------------------------------------------------------------------
  // API calls
  // ---------------------------------------------------------------------------

  const saveBasicInfo = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest("/api/onboarding/profile", {
        method: "POST",
        body: JSON.stringify({
          firstName,
          lastName,
          dateOfBirth,
          rolePreference,
        }),
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => ({ error: "Unable to save profile" }))) as {
          error?: string;
        };
        setError(data.error ?? "Unable to save profile");
        setLoading(false);
        return false;
      }

      const data = (await response.json()) as { completedSteps?: string[] };
      if (data.completedSteps) {
        setCompletedSteps(data.completedSteps);
      }

      setLoading(false);
      return true;
    } catch {
      setError("Network error. Please try again.");
      setLoading(false);
      return false;
    }
  }, [apiRequest, firstName, lastName, dateOfBirth, rolePreference]);

  const saveOptionalStep = useCallback(
    async (step: "school_info" | "interests", data: Record<string, unknown>) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiRequest("/api/onboarding/optional", {
          method: "POST",
          body: JSON.stringify({ step, data }),
        });

        if (!response.ok) {
          const resData = (await response.json().catch(() => ({ error: "Unable to save" }))) as {
            error?: string;
          };
          setError(resData.error ?? "Unable to save");
          setLoading(false);
          return false;
        }

        const resData = (await response.json()) as { completedSteps?: string[] };
        if (resData.completedSteps) {
          setCompletedSteps(resData.completedSteps);
        }

        setLoading(false);
        return true;
      } catch {
        setError("Network error. Please try again.");
        setLoading(false);
        return false;
      }
    },
    [apiRequest]
  );

  const completeOnboarding = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest("/api/onboarding/complete", {
        method: "POST",
      });

      if (!response.ok) {
        const data = (await response
          .json()
          .catch(() => ({ error: "Unable to complete onboarding" }))) as { error?: string };
        setError(data.error ?? "Unable to complete onboarding");
        setLoading(false);
        return;
      }

      setLoading(false);
      onComplete();
    } catch {
      setError("Network error. Please try again.");
      setLoading(false);
    }
  }, [apiRequest, onComplete]);

  // ---------------------------------------------------------------------------
  // Step handlers
  // ---------------------------------------------------------------------------

  const handleBasicInfoSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!rolePreference) {
      setRoleError("Please select a role to continue.");
      return;
    }
    setRoleError(null);
    const success = await saveBasicInfo();
    if (success) {
      setCurrentStep("school_info");
    }
  };

  const handleSchoolInfoSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const success = await saveOptionalStep("school_info", { schoolName, gradeLevel });
    if (success) {
      setCurrentStep("interests");
    }
  };

  const handleSchoolInfoSkip = (): void => {
    setCurrentStep("interests");
  };

  const handleInterestsSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (selectedInterests.length === 0) {
      // No interests selected — treat as skip
      setCurrentStep("review");
      return;
    }
    const success = await saveOptionalStep("interests", { interests: selectedInterests });
    if (success) {
      setCurrentStep("review");
    }
  };

  const handleInterestsSkip = (): void => {
    setCurrentStep("review");
  };

  const handleAddCustomInterest = (): void => {
    const trimmed = customInterest.trim();
    if (trimmed && !selectedInterests.includes(trimmed) && selectedInterests.length < 20) {
      setSelectedInterests([...selectedInterests, trimmed]);
      setCustomInterest("");
    }
  };

  const toggleInterest = (interest: string): void => {
    setSelectedInterests((prev) =>
      prev.includes(interest) ? prev.filter((i) => i !== interest) : [...prev, interest]
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="container mx-auto max-w-2xl px-4 py-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Welcome to Binaism</h1>
            <p className="mt-2 text-gray-600">Let&apos;s set up your profile to get started.</p>
          </div>
          <button
            type="button"
            onClick={onSignOut}
            className="mt-1 inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Sign out
          </button>
        </div>

        {/* Progress indicator */}
        <div className="mt-6 flex items-center gap-2">
          {STEPS.map((step, index) => (
            <div key={step} className="flex items-center gap-2">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                  completedSteps.includes(step) || index < currentStepIndex
                    ? "bg-green-600 text-white"
                    : index === currentStepIndex
                      ? "bg-gray-900 text-white"
                      : "bg-gray-200 text-gray-500"
                }`}
              >
                {completedSteps.includes(step) || index < currentStepIndex ? "✓" : index + 1}
              </div>
              <span
                className={`hidden text-sm sm:inline ${
                  index === currentStepIndex ? "font-semibold text-gray-900" : "text-gray-500"
                }`}
              >
                {STEP_LABELS[step]}
              </span>
              {index < STEPS.length - 1 ? (
                <div className="mx-1 h-px w-6 bg-gray-300 sm:w-10" />
              ) : null}
            </div>
          ))}
        </div>

        {/* Error display */}
        {error ? (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3" role="alert">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : null}

        {/* Step 1: Basic Info */}
        {currentStep === "basic_info" ? (
          <form
            ref={formRef}
            tabIndex={-1}
            className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm outline-none"
            onKeyDown={handleFormKeyDown}
            onSubmit={(event) => {
              void handleBasicInfoSubmit(event);
            }}
          >
            <h2 className="text-lg font-semibold text-gray-900">Tell us about yourself</h2>
            <p className="mt-1 text-sm text-gray-500">
              This information is required to set up your account.
            </p>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">First name</label>
                <input
                  type="text"
                  required
                  maxLength={100}
                  value={firstName}
                  onChange={(event) => {
                    setFirstName(event.target.value);
                  }}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500"
                  placeholder="Jane"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last name</label>
                <input
                  type="text"
                  required
                  maxLength={100}
                  value={lastName}
                  onChange={(event) => {
                    setLastName(event.target.value);
                  }}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Date of birth</label>
              <input
                type="date"
                required
                value={dateOfBirth}
                onChange={(event) => {
                  setDateOfBirth(event.target.value);
                }}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500"
              />
              {isUnderAge ? (
                <p className="mt-1 text-xs text-red-600">You must be at least 18 years old.</p>
              ) : null}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                How will you use Binaism?
              </label>
              <div className="mt-2 flex gap-4" role="radiogroup" aria-label="Role preference">
                <button
                  type="button"
                  role="radio"
                  aria-checked={rolePreference === "student"}
                  onClick={() => {
                    setRolePreference("student");
                    setRoleError(null);
                  }}
                  className={`flex items-center gap-2 rounded-md border px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${
                    rolePreference === "student" ? "border-gray-900 bg-gray-50" : "border-gray-300"
                  }`}
                >
                  <span
                    className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                      rolePreference === "student" ? "border-gray-900" : "border-gray-400"
                    }`}
                  >
                    {rolePreference === "student" ? (
                      <span className="h-2 w-2 rounded-full bg-gray-900" />
                    ) : null}
                  </span>
                  <div>
                    <span className="text-sm font-medium text-gray-900">Student</span>
                    <p className="text-xs text-gray-500">I&apos;m looking for guidance</p>
                  </div>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={rolePreference === "counselor"}
                  onClick={() => {
                    setRolePreference("counselor");
                    setRoleError(null);
                  }}
                  className={`flex items-center gap-2 rounded-md border px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${
                    rolePreference === "counselor"
                      ? "border-gray-900 bg-gray-50"
                      : "border-gray-300"
                  }`}
                >
                  <span
                    className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                      rolePreference === "counselor" ? "border-gray-900" : "border-gray-400"
                    }`}
                  >
                    {rolePreference === "counselor" ? (
                      <span className="h-2 w-2 rounded-full bg-gray-900" />
                    ) : null}
                  </span>
                  <div>
                    <span className="text-sm font-medium text-gray-900">Counselor</span>
                    <p className="text-xs text-gray-500">I help students succeed</p>
                  </div>
                </button>
              </div>
              {roleError ? (
                <p className="mt-1 text-xs text-red-600">{roleError}</p>
              ) : !rolePreference ? (
                <p className="mt-1 text-xs text-gray-500">Please select a role to continue.</p>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-6 inline-flex items-center rounded-md bg-gray-900 px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {loading ? "Saving..." : "Continue"}
            </button>
          </form>
        ) : null}

        {/* Step 2: School Info (Optional) */}
        {currentStep === "school_info" ? (
          <form
            ref={formRef}
            tabIndex={-1}
            className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm outline-none"
            onKeyDown={handleFormKeyDown}
            onSubmit={(event) => {
              void handleSchoolInfoSubmit(event);
            }}
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">School Information</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Optional — you can always add this later.
                </p>
              </div>
              <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                Optional
              </span>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">School name</label>
              <input
                type="text"
                maxLength={200}
                value={schoolName}
                onChange={(event) => {
                  setSchoolName(event.target.value);
                }}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500"
                placeholder="Springfield High School"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Grade level</label>
              <select
                value={gradeLevel}
                onChange={(event) => {
                  setGradeLevel(event.target.value);
                }}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500"
              >
                <option value="">Select grade level</option>
                <option value="9th">9th Grade</option>
                <option value="10th">10th Grade</option>
                <option value="11th">11th Grade</option>
                <option value="12th">12th Grade</option>
                <option value="college_freshman">College Freshman</option>
                <option value="college_sophomore">College Sophomore</option>
                <option value="college_junior">College Junior</option>
                <option value="college_senior">College Senior</option>
                <option value="graduate">Graduate Student</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setCurrentStep("basic_info");
                }}
                disabled={loading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center rounded-md bg-gray-900 px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {loading ? "Saving..." : "Save & Continue"}
              </button>
              <button
                type="button"
                onClick={handleSchoolInfoSkip}
                disabled={loading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Skip for now
              </button>
            </div>
          </form>
        ) : null}

        {/* Step 3: Interests (Optional) */}
        {currentStep === "interests" ? (
          <form
            ref={formRef}
            tabIndex={-1}
            className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm outline-none"
            onKeyDown={handleFormKeyDown}
            onSubmit={(event) => {
              void handleInterestsSubmit(event);
            }}
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">What are you interested in?</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Optional — helps us personalize your experience.
                </p>
              </div>
              <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                Optional
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {SUGGESTED_INTERESTS.map((interest) => (
                <button
                  key={interest}
                  type="button"
                  onClick={() => {
                    toggleInterest(interest);
                  }}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                    selectedInterests.includes(interest)
                      ? "bg-gray-900 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {interest}
                </button>
              ))}
            </div>

            {/* Custom interest input */}
            <div className="mt-4 flex gap-2">
              <input
                type="text"
                maxLength={100}
                value={customInterest}
                onChange={(event) => {
                  setCustomInterest(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    if (customInterest.trim()) {
                      // Text present — add the custom interest instead of submitting
                      event.preventDefault();
                      handleAddCustomInterest();
                    }
                    // Empty input — let Enter propagate to submit the form
                  }
                }}
                className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500"
                placeholder="Add your own interest..."
              />
              <button
                type="button"
                onClick={handleAddCustomInterest}
                disabled={!customInterest.trim()}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Add
              </button>
            </div>

            {selectedInterests.length > 0 ? (
              <p className="mt-2 text-xs text-gray-500">
                {selectedInterests.length} selected (max 20)
              </p>
            ) : null}

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setCurrentStep("school_info");
                }}
                disabled={loading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center rounded-md bg-gray-900 px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {loading
                  ? "Saving..."
                  : selectedInterests.length > 0
                    ? "Save & Continue"
                    : "Continue"}
              </button>
              <button
                type="button"
                onClick={handleInterestsSkip}
                disabled={loading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Skip for now
              </button>
            </div>
          </form>
        ) : null}

        {/* Step 4: Review & Complete */}
        {currentStep === "review" ? (
          <form
            ref={formRef}
            tabIndex={-1}
            className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm outline-none"
            onKeyDown={handleFormKeyDown}
            onSubmit={(event) => {
              event.preventDefault();
              void completeOnboarding();
            }}
          >
            <h2 className="text-lg font-semibold text-gray-900">You&apos;re all set!</h2>
            <p className="mt-1 text-sm text-gray-500">
              Review your choices below. You can update your profile anytime.
            </p>

            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-green-600">✓</span>
                <span className="text-sm text-gray-700">Basic profile information saved</span>
              </div>
              <div className="flex items-center gap-2">
                {completedSteps.includes("school_info") ? (
                  <>
                    <span className="text-green-600">✓</span>
                    <span className="text-sm text-gray-700">School information saved</span>
                  </>
                ) : (
                  <>
                    <span className="text-gray-400">–</span>
                    <span className="text-sm text-gray-500">
                      School information skipped{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentStep("school_info");
                        }}
                        className="text-gray-700 underline"
                      >
                        (go back)
                      </button>
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                {completedSteps.includes("interests") ? (
                  <>
                    <span className="text-green-600">✓</span>
                    <span className="text-sm text-gray-700">Interests saved</span>
                  </>
                ) : (
                  <>
                    <span className="text-gray-400">–</span>
                    <span className="text-sm text-gray-500">
                      Interests skipped{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentStep("interests");
                        }}
                        className="text-gray-700 underline"
                      >
                        (go back)
                      </button>
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setCurrentStep("interests");
                }}
                disabled={loading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center rounded-md bg-gray-900 px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {loading ? "Finishing..." : "Get Started"}
              </button>
            </div>
          </form>
        ) : null}
      </main>
    </div>
  );
}
